import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import SaveNicheButton from "@/components/SaveNicheButton";
import ScanLoader from "@/components/ScanLoader";
import {
  getLowRevenue,
  triggerLowRevenueScan,
  getLowRevenueScan,
} from "@/lib/api";

/**
 * LowRevenue — the "$10k to $50k monthly" leaderboard.
 *
 * Entry-tier channels — proven monetization but at modest scale. Best for
 * creators just validating "can I actually monetize at all in this niche".
 * Lower competition + smaller audience size makes these the easiest to
 * out-compete with better content.
 */
function nFmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}
function ageLabel(days) {
  if (days == null) return "—";
  if (days < 30) return `${Math.max(1, Math.round(days))}d old`;
  if (days < 365) return `${Math.round(days / 30)}mo old`;
  return `${(days / 365).toFixed(1)}yr old`;
}
function durationLabel(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (sec < 60) return `${sec}s`;
  if (m < 60) return `${m}m ${String(s).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

export default function LowRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [page, setPage] = useState(0);
  const pollRef = useRef(null);
  const PAGE_SIZE = 20;

  useEffect(() => () => clearInterval(pollRef.current), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getLowRevenue());
      setPage(0);
    }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runScan = async (force = false) => {
    setScanning(true);
    try {
      const { scan_id, status, from_cache } = await triggerLowRevenueScan(force);
      if (from_cache && status === "complete") {
        toast.success("Loaded latest cached results");
        await load();
        setScanning(false);
        return;
      }
      toast.info("Scanning YouTube for $50k–$100k/mo channels — ~60s");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const doc = await getLowRevenueScan(scan_id);
          if (doc.status === "complete") {
            clearInterval(pollRef.current);
            setScanning(false);
            await load();
            toast.success(`Found ${doc.channel_count} low-revenue channels`);
          } else if (doc.status === "failed") {
            clearInterval(pollRef.current);
            setScanning(false);
            toast.error(doc.error || "Scan failed");
          }
        } catch {
          clearInterval(pollRef.current);
          setScanning(false);
          toast.error("Lost connection");
        }
      }, 4000);
    } catch (err) {
      setScanning(false);
      toast.error(err?.response?.data?.detail || "Couldn't start scan");
    }
  };

  const channels = data?.channels || [];
  const hasResults = channels.length > 0;
  const stale = data?.stale;
  const age = data?.age_hours;
  const floorK = Math.round((data?.floor_usd || 10000) / 1000);
  const ceilingK = Math.round((data?.ceiling_usd || 50000) / 1000);
  const totalPages = Math.max(1, Math.ceil(channels.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const visibleChannels = channels.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div data-testid="low-revenue-page" className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 1 · LOW-REVENUE · ${floorK}K–${ceilingK}K/MO
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        ${floorK}K to ${ceilingK}K monthly channels.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-6 leading-relaxed">
        Channels earning{" "}
        <span className="text-emerald-300 font-semibold">
          ${floorK},000 – ${ceilingK},000 per month
        </span>{" "}
        — entry-tier creators with proven monetization at modest scale.
        Lower competition + smaller audiences make these the easiest channels
        to out-compete with better content. Cached for 48h.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <button
          data-testid="lr-scan-btn"
          onClick={() => runScan(false)}
          disabled={scanning}
          className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
        >
          {scanning ? "Scanning…" : hasResults ? "▸ Refresh" : "▸ Run scan"}
        </button>
        {hasResults && (
          <button
            data-testid="lr-force-btn"
            onClick={() => runScan(true)}
            disabled={scanning}
            className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
          >
            ↻ Force fresh scan
          </button>
        )}
        {hasResults && age != null && (
          <span className={`font-mono text-[11px] uppercase tracking-[0.25em] ${stale ? "text-amber-400" : "text-zinc-500"}`}>
            cached {age < 1 ? `${Math.round(age * 60)}m` : `${age}h`} ago{stale ? " · stale" : ""}
          </span>
        )}
      </div>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">[recon] loading<span className="blink">_</span></div>
      ) : scanning ? (
        <div className="border border-zinc-800 rounded-sm py-6" data-testid="lr-scanning">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-4 text-center">
            // SCANNING YOUTUBE FOR ${floorK}K–${ceilingK}K/MO CHANNELS
          </div>
          <ScanLoader
            stages={[
              "Querying 15 broad seed buckets",
              "Resolving channels from top videos",
              "Pulling channel stats (subs · age · views)",
              "Sampling recent uploads · duration mix",
              "Computing format-aware revenue (RPM × views)",
              `Keeping only $${floorK}K–$${ceilingK}K/mo band`,
              "AI-tagging each channel's niche (Claude)",
            ]}
            stageDurationMs={6000}
            subtitle="~60–90s · scanning global YouTube"
          />
        </div>
      ) : !hasResults ? (
        <div className="border border-zinc-800 rounded-sm p-10 text-center" data-testid="lr-empty">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">// NO SCAN YET</div>
          <p className="text-zinc-300 text-sm mb-2">
            Click <b>Run scan</b> to surface channels earning between ${floorK}K and ${ceilingK}K per month.
          </p>
        </div>
      ) : (
        <>
        <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="lr-list">
          <div className="grid grid-cols-12 px-4 py-3 bg-zinc-900 border-b-2 border-yellow-400/30 font-mono text-xs uppercase tracking-[0.15em] text-yellow-400 font-bold">
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-3">Channel</div>
            <div className="col-span-1 text-center">Niche</div>
            <div className="col-span-2 text-center">Rev / mo</div>
            <div className="col-span-1 text-center">Subs</div>
            <div className="col-span-1 text-center">Uploads / mo</div>
            <div className="col-span-1 text-center">Avg length</div>
            <div className="col-span-1 text-center">Age</div>
            <div className="col-span-1 text-center">Save</div>
          </div>
          {visibleChannels.map((c, i) => {
            const rank = pageStart + i;
            return (
              <div
                key={c.channel_id}
                data-testid={`lr-row-${c.channel_id}`}
                className="grid grid-cols-12 px-4 py-4 border-b border-zinc-900 last:border-b-0 items-center hover:bg-zinc-950/60 transition-colors recon-row-in"
                style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
              >
                <div className="col-span-1 text-center font-display text-2xl font-black text-yellow-400">
                  {String(rank + 1).padStart(2, "0")}
                </div>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="col-span-3 flex items-center gap-3 min-w-0 hover:text-yellow-400"
                >
                  {c.thumbnail ? (
                    <img src={c.thumbnail} alt={c.name} className="h-10 w-10 rounded-full shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-zinc-800 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm text-white font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-zinc-300 truncate">{c.description}</div>
                  </div>
                </a>
                <div className="col-span-1 flex justify-center">
                  {c.niche ? (
                    <span
                      data-testid={`lr-niche-${c.channel_id}`}
                      className="inline-block max-w-full font-mono text-[10px] uppercase tracking-[0.1em] text-yellow-300 bg-yellow-400/10 border border-yellow-400/30 px-2 py-1 rounded-sm leading-tight text-center"
                    >
                      {c.niche}
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] text-zinc-600">—</span>
                  )}
                </div>
                <div className="col-span-2 text-center font-display text-xl font-bold text-emerald-300">
                  ${nFmt(c.est_monthly_revenue)}
                </div>
                <div className="col-span-1 text-center text-sm font-mono text-zinc-100">
                  {nFmt(c.subscribers)}
                </div>
                <div className="col-span-1 text-center">
                  <div className="text-sm font-mono text-zinc-100">
                    {c.uploads_per_month != null ? c.uploads_per_month : "—"}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-400">{c.video_count} total</div>
                </div>
                <div className="col-span-1 text-center text-sm font-mono text-zinc-100">
                  <div>{durationLabel(c.avg_video_length_sec)}</div>
                  {c.format && c.format !== "unknown" && (
                    <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${c.format === "shorts" ? "text-amber-400" : "text-emerald-400"}`}>
                      {c.format === "shorts" ? "Shorts" : "Long-form"}
                    </div>
                  )}
                </div>
                <div className="col-span-1 text-center text-xs font-mono text-zinc-200">
                  {ageLabel(c.channel_age_days)}
                </div>
                <div className="col-span-1 text-center">
                  <SaveNicheButton channel={c} source="low_revenue" />
                </div>
              </div>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div
            data-testid="lr-pagination"
            className="mt-5 flex items-center justify-between flex-wrap gap-3 border border-zinc-900 px-4 py-3 rounded-sm"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
              Showing{" "}
              <span className="text-yellow-400 font-bold">
                {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, channels.length)}
              </span>{" "}
              of <span className="text-white font-bold">{channels.length}</span>{" "}
              channels
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="lr-page-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="font-mono text-xs uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 disabled:text-zinc-700 disabled:border-zinc-900 disabled:hover:border-zinc-900 px-3 py-1.5 rounded-sm transition-colors"
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i).map((p) => (
                <button
                  key={p}
                  data-testid={`lr-page-${p}`}
                  onClick={() => setPage(p)}
                  className={`font-mono text-xs px-2.5 py-1.5 rounded-sm transition-colors border ${
                    p === safePage
                      ? "bg-yellow-400 text-black border-yellow-400 font-bold"
                      : "text-zinc-400 border-zinc-800 hover:border-yellow-400 hover:text-yellow-400"
                  }`}
                >
                  {p + 1}
                </button>
              ))}
              <button
                data-testid="lr-page-next"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                className="font-mono text-xs uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 disabled:text-zinc-700 disabled:border-zinc-900 disabled:hover:border-zinc-900 px-3 py-1.5 rounded-sm transition-colors"
              >
                Next →
              </button>
            </div>
          </div>
        )}
        </>
      )}

      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 leading-relaxed">
        revenue est = avg_monthly_views × RPM ÷ 1000 · long-form RPM $3 · shorts RPM $0.07
      </p>
    </div>
  );
}
